// =============================================================
// FLOW (Wave 7b): Meter READINGS — ported from scripts/perpetuum-desktop-
// sections2.jsx (SecMeters), now reading + WRITING the REAL /api/v1.
//
// Pick a device → load its measurement points (GET /devices/:id/measurement-
// points, which already carry the latest value + the LL/L/H/HH template
// thresholds + the server-computed band) → enter a new reading per point
// (POST /devices/:id/measurement-readings). The in/out-of-tolerance feedback
// reuses the SAME core `classifyTolerance` rule the Device-Card Measurements tab
// uses (single source of truth — no parallel implementation). After a write the
// points re-query so the latest value + band update. Gated by dev:read (view) /
// dev:write (record), and by the `measurements` feature flag (nav hidden when
// off). No synthetic data.
// =============================================================

import { useEffect, useMemo, useState } from "react";
import { classifyTolerance } from "@perpetuum/core";
import type { DeviceMeasurementPoint } from "@perpetuum/core";
import { useDevices, useDeviceMeasurementPoints, useAddMeasurementReading } from "../lib/queries.js";
import { useAuth } from "../app/AuthContext.js";
import { useChrome } from "../app/ChromeContext.js";
import { useScopedT } from "../lib/i18n-keys.js";
import { Btn, Icon, ICONS, ThresholdBar } from "../components/primitives.js";
import { EmptyState } from "../components/EmptyState.js";
import { Skeleton } from "../components/Skeleton.js";

/** Map a tolerance band → the status chip class + i18n message key. */
const BAND_META: Record<"ok" | "warn" | "alarm" | "none", { cls: string; key: string }> = {
  ok: { cls: "ok", key: "meters.inTolerance" },
  warn: { cls: "warn", key: "meters.warn" },
  alarm: { cls: "crit", key: "meters.alarm" },
  none: { cls: "", key: "meters.noReading" },
};

export function Meters() {
  const t = useScopedT();
  const { can } = useAuth();
  const { toast } = useChrome();

  const devicesQ = useDevices();
  const devices = devicesQ.data ?? [];

  const [deviceId, setDeviceId] = useState<string>("");
  // Default to the first device once loaded.
  useEffect(() => {
    if (!deviceId && devices[0]) setDeviceId(devices[0].id);
  }, [devices, deviceId]);

  const pointsQ = useDeviceMeasurementPoints(deviceId || null, !!deviceId);
  const addReading = useAddMeasurementReading();
  const points = pointsQ.data ?? [];

  const canWrite = can("dev", "write");

  // Per-point draft values (keyed by point id) + the locally-classified band so
  // the feedback reacts as the operator types, BEFORE the write round-trips.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  // Reset drafts when switching device.
  useEffect(() => {
    setDrafts({});
  }, [deviceId]);

  const deviceName = useMemo(() => devices.find((d) => d.id === deviceId)?.name ?? "", [devices, deviceId]);

  const onRecord = async (p: DeviceMeasurementPoint) => {
    if (!canWrite) {
      toast(t("meters.noPerm"), "warning");
      return;
    }
    const raw = drafts[p.id] ?? "";
    const value = parseFloat(raw.replace(",", "."));
    if (!Number.isFinite(value)) return;
    try {
      const res = await addReading.mutateAsync({ deviceId, pointId: p.id, value });
      const meta = BAND_META[res.band];
      toast(`${t("meters.recorded")} — ${t(meta.key)}`, res.band === "alarm" ? "warning" : res.band === "warn" ? "warning" : "success");
      setDrafts((d) => ({ ...d, [p.id]: "" }));
    } catch {
      toast(t("login.err.network_error"), "error");
    }
  };

  return (
    <div>
      <h2 className="d-h2">{t("meters.title")}</h2>
      <p className="d-sub">{t("meters.sub")}</p>

      <div className="d-card" style={{ marginBottom: 18 }}>
        <div className="d-card-body" style={{ maxWidth: 420 }}>
          <div className="d-field">
            <label htmlFor="meter-device">{t("meters.device")}</label>
            <div className="field-ctrl">
              <select id="meter-device" data-testid="meter-device" value={deviceId} onChange={(e) => setDeviceId(e.target.value)}>
                <option value="">{t("meters.devicePlaceholder")}</option>
                {devices.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {!deviceId ? (
        <EmptyState icon={ICONS.gauge} title={t("meters.noDevice")} hint={t("meters.noDeviceHint")} />
      ) : pointsQ.isLoading ? (
        <Skeleton variant="card" height={140} count={3} />
      ) : points.length === 0 ? (
        <EmptyState icon={ICONS.gauge} title={t("meters.noPoints")} hint={t("meters.noPointsHint")} />
      ) : (
        <div className="d-card" data-testid="meters-card">
          <div className="d-card-hd">
            <h3>{deviceName}</h3>
            <span className="sp" style={{ flex: 1 }} />
            <span className="ro-badge" data-testid="meters-point-count">
              {points.length}
            </span>
          </div>
          <div className="d-card-body">
            {points.map((p) => {
              const draft = drafts[p.id] ?? "";
              const draftNum = parseFloat(draft.replace(",", "."));
              const hasDraft = draft.trim() !== "" && Number.isFinite(draftNum);
              // Classify the DRAFT (if any) via the core rule; otherwise fall
              // back to the server's band for the latest stored value.
              const draftBand = hasDraft
                ? classifyTolerance({ ll: p.ll, l: p.l, h: p.h, hh: p.hh }, draftNum).band
                : null;
              const band = draftBand ?? p.band;
              const meta = BAND_META[band];
              // The bar marker follows the draft if present, else the latest value,
              // else parks at the centre of the normal zone (no fabricated reading).
              const markerValue = hasDraft ? draftNum : p.latestValue ?? (p.l + p.h) / 2;
              return (
                <div key={p.id} className="d-card" style={{ marginBottom: 12 }} data-testid={`meter-point-${p.id}`}>
                  <div className="d-card-body">
                    <ThresholdBar
                      p={{ name: p.name, unit: p.unit ?? undefined, ll: p.ll, l: p.l, h: p.h, hh: p.hh, value: markerValue }}
                    />
                    <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginTop: 10 }}>
                      <div style={{ flex: 1, minWidth: 120 }}>
                        <div className="dt-note">
                          {t("meters.latest")}:{" "}
                          <span className="mono">
                            {p.latestValue == null ? "—" : p.latestValue}
                            {p.unit ? ` ${p.unit}` : ""}
                          </span>
                          {p.latestRecordedAt && (
                            <span style={{ color: "var(--sys-fg-3)" }}> · {new Date(p.latestRecordedAt).toLocaleString()}</span>
                          )}
                        </div>
                      </div>
                      <div className="d-field" style={{ width: 150 }}>
                        <label htmlFor={`meter-val-${p.id}`}>{t("meters.newValue")}</label>
                        <div className="field-ctrl">
                          <input
                            id={`meter-val-${p.id}`}
                            data-testid={`meter-val-${p.id}`}
                            type="number"
                            step="any"
                            inputMode="decimal"
                            value={draft}
                            onChange={(e) => setDrafts((d) => ({ ...d, [p.id]: e.target.value }))}
                          />
                        </div>
                      </div>
                      <Btn
                        variant="primary"
                        size="core"
                        data-testid={`meter-record-${p.id}`}
                        disabled={!canWrite || !hasDraft || addReading.isPending}
                        onClick={() => void onRecord(p)}
                      >
                        <Icon d={ICONS.check} size={16} />
                        {t("meters.record")}
                      </Btn>
                    </div>
                    {/* Tolerance feedback — uses the core-classified band (draft or latest). */}
                    <div className={`meter-band ${meta.cls}`} data-testid={`meter-band-${p.id}`} style={{ marginTop: 8, fontWeight: 600 }}>
                      <span className={`mp-val ${meta.cls}`}>{t(meta.key)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
